import { Box, Typography, Avatar, TextField, Button, Divider, Switch, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress, Snackbar, Alert } from '@mui/material';
import { useState, useEffect } from 'react';
import PersonIcon from '@mui/icons-material/Person';
import LockIcon from '@mui/icons-material/Lock';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { fetchUserById, updateUserDetails } from '../api/authApi';

const NAV = [
  { id: 'profile', label: 'Your Profile', icon: <PersonIcon />, color: '#6366f1' },
  { id: 'password', label: 'Change Password', icon: <LockIcon />, color: '#f59e0b' },
  { id: 'delete', label: 'Delete Account', icon: <DeleteIcon />, color: '#ef4444' },
];

export default function Account() {
  const [active, setActive] = useState('profile');
  const activeNav = NAV.find(n => n.id === active);

  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 70px)', gap: 0, overflow: 'hidden' }}>
      {/* Sidebar */}
      <Box sx={{ width: 260, flexShrink: 0, bgcolor: '#fafbfc', borderRight: '1px solid #f0f0f0', p: 2, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Typography sx={{ fontWeight: 800, fontSize: 18, color: '#111827', px: 1.5, mb: 2 }}>Account</Typography>
        {NAV.map(item => (
          <Box key={item.id} onClick={() => setActive(item.id)}
            sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 1.5, py: 1.2, borderRadius: 2, cursor: 'pointer',
              bgcolor: active === item.id ? `${item.color}10` : 'transparent',
              border: active === item.id ? `1px solid ${item.color}25` : '1px solid transparent',
              transition: 'all 0.15s', '&:hover': { bgcolor: `${item.color}08` } }}>
            <Box sx={{ color: active === item.id ? item.color : '#9ca3af', display: 'flex' }}>{item.icon}</Box>
            <Typography sx={{ fontSize: 13, fontWeight: active === item.id ? 700 : 500, color: active === item.id ? item.color : '#4b5563' }}>{item.label}</Typography>
          </Box>
        ))}
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 4 }}>
        <Box sx={{ maxWidth: 600 }}>
          <Typography sx={{ fontWeight: 800, fontSize: 22, color: '#111827', mb: 0.5 }}>{activeNav?.label}</Typography>
          <Typography sx={{ fontSize: 13, color: '#9ca3af', mb: 3 }}>Manage your account settings</Typography>
          {active === 'profile' && <ProfileTab />}
          {active === 'password' && <PasswordTab />}
          {active === 'delete' && <DeleteTab />}
        </Box>
      </Box>
    </Box>
  );
}

function ProfileTab() {
  const [user, setUser] = useState({ fullName: '', email: '', phone: '', website: '', gst: '' });
  const [plan, setPlan] = useState(null);
  const [usage, setUsage] = useState({ bots: 0, chats: 0 });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snack, setSnack] = useState({ open: false, msg: '', type: 'success' });

  useEffect(() => {
    const userId = localStorage.getItem('userId') || JSON.parse(localStorage.getItem('user'))?._id;
    if (!userId) return;
    Promise.all([
      fetchUserById(userId),
      fetch('/api/bots', { credentials: "include" }).then(r => r.json()).catch(() => []),
      fetch('/api/conversation/list?limit=1', { credentials: "include" }).then(r => r.json()).catch(() => ({})),
    ]).then(([userData, bots, convos]) => {
      if (userData) {
        setUser({ fullName: userData.fullName || '', email: userData.email || '', phone: userData.phone || '', website: userData.website || '', gst: userData.gst || '' });
        setPlan(userData.planLimits || null);
      }
      setUsage({ bots: Array.isArray(bots) ? bots.length : 0, chats: convos?.total || 0 });
    }).finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const userId = localStorage.getItem('userId') || JSON.parse(localStorage.getItem('user'))?._id;
      const base = process.env.REACT_APP_AUTH_API ? process.env.REACT_APP_AUTH_API.replace('/auth', '') : 'http://localhost:5000/api';
      const res = await fetch(`${base}/auth/user/${userId}/profile`, {
        method: 'PUT',
        credentials: "include",
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(user),
      });
      if (res.ok) setSnack({ open: true, msg: 'Profile updated successfully!', type: 'success' });
      else setSnack({ open: true, msg: 'Failed to update profile', type: 'error' });
    } catch { setSnack({ open: true, msg: 'Something went wrong', type: 'error' }); }
    finally { setSaving(false); }
  };

  if (loading) return <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress size={24} /></Box>;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      {/* Avatar */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2.5, bgcolor: '#f8fafc', borderRadius: 3, border: '1px solid #e2e8f0' }}>
        <Avatar sx={{ width: 56, height: 56, background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', fontSize: 22, fontWeight: 700 }}>
          {(user.fullName || 'U')[0].toUpperCase()}
        </Avatar>
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: 16, color: '#111827' }}>{user.fullName || 'User'}</Typography>
          <Typography sx={{ fontSize: 12, color: '#6b7280' }}>{user.email}</Typography>
        </Box>
      </Box>

      {/* Plan Details */}
      {plan && (
        <Box sx={{ p: 2.5, bgcolor: '#f0f9ff', borderRadius: 3, border: '1px solid #bae6fd' }}>
          <Typography sx={{ fontWeight: 700, fontSize: 14, color: '#0369a1', mb: 1.5 }}>📋 Plan Details</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {plan.chatbots != null && <Box sx={{ display: 'flex', justifyContent: 'space-between' }}><Typography sx={{ fontSize: 12, color: '#4b5563' }}>Bots</Typography><Typography sx={{ fontSize: 12, fontWeight: 700, color: '#111827' }}>{usage.bots} / {plan.chatbots === -1 ? '∞' : plan.chatbots}</Typography></Box>}
            {plan.chatsPerMonth != null && <Box sx={{ display: 'flex', justifyContent: 'space-between' }}><Typography sx={{ fontSize: 12, color: '#4b5563' }}>Chats/Month</Typography><Typography sx={{ fontSize: 12, fontWeight: 700, color: '#111827' }}>{usage.chats} / {plan.chatsPerMonth === -1 ? '∞' : plan.chatsPerMonth}</Typography></Box>}
            {plan.liveAgents != null && <Box sx={{ display: 'flex', justifyContent: 'space-between' }}><Typography sx={{ fontSize: 12, color: '#4b5563' }}>Live Agents</Typography><Typography sx={{ fontSize: 12, fontWeight: 700, color: '#111827' }}>{plan.liveAgents === -1 ? 'Unlimited' : plan.liveAgents}</Typography></Box>}
            {plan.aiMessages != null && <Box sx={{ display: 'flex', justifyContent: 'space-between' }}><Typography sx={{ fontSize: 12, color: '#4b5563' }}>AI Messages/Month</Typography><Typography sx={{ fontSize: 12, fontWeight: 700, color: '#111827' }}>{plan.aiMessages === -1 ? 'Unlimited' : plan.aiMessages}</Typography></Box>}
          </Box>
        </Box>
      )}

      {/* Fields */}
      <Box sx={{ p: 2.5, bgcolor: '#f8fafc', borderRadius: 3, border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box>
          <Typography sx={{ fontWeight: 600, fontSize: 12, color: '#374151', mb: 0.5 }}>Full Name</Typography>
          <TextField fullWidth size="small" value={user.fullName} onChange={e => setUser({ ...user, fullName: e.target.value })}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fff' } }} />
        </Box>
        <Box>
          <Typography sx={{ fontWeight: 600, fontSize: 12, color: '#374151', mb: 0.5 }}>Email</Typography>
          <TextField fullWidth size="small" value={user.email} disabled
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#f3f4f6' } }} />
        </Box>
        <Box>
          <Typography sx={{ fontWeight: 600, fontSize: 12, color: '#374151', mb: 0.5 }}>Phone</Typography>
          <TextField fullWidth size="small" value={user.phone} onChange={e => setUser({ ...user, phone: e.target.value })}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fff' } }} />
        </Box>
        <Box>
          <Typography sx={{ fontWeight: 600, fontSize: 12, color: '#374151', mb: 0.5 }}>Website</Typography>
          <TextField fullWidth size="small" value={user.website} onChange={e => setUser({ ...user, website: e.target.value })}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fff' } }} />
        </Box>
        <Box>
          <Typography sx={{ fontWeight: 600, fontSize: 12, color: '#374151', mb: 0.5 }}>GST Number</Typography>
          <TextField fullWidth size="small" value={user.gst} onChange={e => setUser({ ...user, gst: e.target.value })} placeholder="Enter GST number"
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fff' } }} />
        </Box>
      </Box>

      {/* Save */}
      <Button variant="contained" onClick={handleSave} disabled={saving} startIcon={<SaveIcon />}
        sx={{ alignSelf: 'flex-start', borderRadius: 2, textTransform: 'none', fontWeight: 700, px: 3,
          background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', '&:hover': { background: 'linear-gradient(135deg, #4f46e5, #7c3aed)' } }}>
        {saving ? 'Saving...' : 'Save Changes'}
      </Button>

      <Snackbar open={snack.open} autoHideDuration={3000} onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert severity={snack.type} onClose={() => setSnack({ ...snack, open: false })} sx={{ fontWeight: 600 }}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}

function PasswordTab() {
  const [current, setCurrent] = useState('');
  const [newPass, setNewPass] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  const handleChange = async () => {
    if (newPass !== confirm) { setMsg('Passwords do not match'); return; }
    if (newPass.length < 8) { setMsg('Password must be at least 8 characters'); return; }
    setSaving(true); setMsg('');
    try {
      const res = await fetch('/api/auth/change-password', {
        method: 'POST',
        credentials: "include",
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ currentPassword: current, newPassword: newPass }),
      });
      const data = await res.json();
      if (res.ok) { setMsg('✅ Password changed successfully'); setCurrent(''); setNewPass(''); setConfirm(''); }
      else setMsg(data.message || 'Failed to change password');
    } catch { setMsg('Error changing password'); }
    finally { setSaving(false); }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      <Box sx={{ p: 2.5, bgcolor: '#f8fafc', borderRadius: 3, border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box>
          <Typography sx={{ fontWeight: 600, fontSize: 12, color: '#374151', mb: 0.5 }}>Current Password</Typography>
          <TextField fullWidth size="small" type={show ? 'text' : 'password'} value={current} onChange={e => setCurrent(e.target.value)}
            InputProps={{ endAdornment: <IconButton size="small" onClick={() => setShow(!show)}>{show ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}</IconButton> }}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fff' } }} />
        </Box>
        <Box>
          <Typography sx={{ fontWeight: 600, fontSize: 12, color: '#374151', mb: 0.5 }}>New Password</Typography>
          <TextField fullWidth size="small" type={show ? 'text' : 'password'} value={newPass} onChange={e => setNewPass(e.target.value)}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fff' } }} />
        </Box>
        <Box>
          <Typography sx={{ fontWeight: 600, fontSize: 12, color: '#374151', mb: 0.5 }}>Confirm New Password</Typography>
          <TextField fullWidth size="small" type={show ? 'text' : 'password'} value={confirm} onChange={e => setConfirm(e.target.value)}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fff' } }} />
        </Box>
        {msg && <Typography sx={{ fontSize: 12, color: msg.includes('✅') ? '#059669' : '#ef4444', fontWeight: 600 }}>{msg}</Typography>}
      </Box>
      <Button variant="contained" onClick={handleChange} disabled={saving || !current || !newPass}
        sx={{ alignSelf: 'flex-start', borderRadius: 2, textTransform: 'none', fontWeight: 700, px: 3,
          background: 'linear-gradient(135deg, #f59e0b, #f97316)' }}>
        {saving ? 'Changing...' : 'Change Password'}
      </Button>
    </Box>
  );
}

function DeleteTab() {
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState('');

  const handleDelete = async () => {
    if (confirm !== 'DELETE') return;
    try {
      const base = process.env.REACT_APP_AUTH_API ? process.env.REACT_APP_AUTH_API.replace('/auth', '') : 'http://localhost:5000/api';
      await fetch(`${base}/auth/delete-account`, { method: 'DELETE', credentials: "include" });
      localStorage.clear();
      window.location.href = '/';
    } catch {}
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      <Box sx={{ p: 2.5, bgcolor: '#fef2f2', borderRadius: 3, border: '1px solid #fecaca' }}>
        <Typography sx={{ fontWeight: 700, fontSize: 14, color: '#dc2626', mb: 0.5 }}>⚠️ Danger Zone</Typography>
        <Typography sx={{ fontSize: 12, color: '#7f1d1d' }}>
          Deleting your account will permanently remove all your bots, conversations, and data. This action cannot be undone.
        </Typography>
      </Box>
      <Button variant="contained" color="error" onClick={() => setOpen(true)}
        sx={{ alignSelf: 'flex-start', borderRadius: 2, textTransform: 'none', fontWeight: 700, px: 3 }}>
        Delete My Account
      </Button>

      <Dialog open={open} onClose={() => { setOpen(false); setConfirm(''); }} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 4, overflow: 'hidden', boxShadow: '0 25px 60px rgba(0,0,0,0.15)', backdropFilter: 'blur(10px)' } }}>
        {/* Header */}
        <Box sx={{ background: 'linear-gradient(135deg, #ef4444, #dc2626)', px: 3, py: 2.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ width: 40, height: 40, borderRadius: '12px', bgcolor: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <DeleteIcon sx={{ color: '#fff', fontSize: 22 }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: 16, color: '#fff' }}>Delete Account</Typography>
            <Typography sx={{ fontSize: 11, color: 'rgba(255,255,255,0.7)' }}>This action is permanent</Typography>
          </Box>
        </Box>
        {/* Content */}
        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ p: 2, bgcolor: 'rgba(254,242,242,0.7)', backdropFilter: 'blur(8px)', borderRadius: 2.5, border: '1px solid #fecaca', mb: 2.5 }}>
            <Typography sx={{ fontSize: 13, color: '#991b1b', lineHeight: 1.6 }}>
              All your <b>bots</b>, <b>conversations</b>, <b>leads</b>, and <b>settings</b> will be permanently deleted. You will not be able to recover any data.
            </Typography>
          </Box>
          <Typography sx={{ fontSize: 13, color: '#4b5563', mb: 1.5, fontWeight: 600 }}>
            Type <Box component="span" sx={{ px: 1, py: 0.3, bgcolor: '#fee2e2', borderRadius: 1, fontFamily: 'monospace', fontWeight: 800, color: '#dc2626' }}>DELETE</Box> to confirm:
          </Typography>
          <TextField fullWidth size="small" value={confirm} onChange={e => setConfirm(e.target.value)} placeholder="Type DELETE here"
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'rgba(255,255,255,0.8)', backdropFilter: 'blur(4px)', '&.Mui-focused fieldset': { borderColor: '#ef4444' } } }} />
        </DialogContent>
        {/* Actions */}
        <DialogActions sx={{ px: 3, pb: 3, pt: 0, gap: 1 }}>
          <Button onClick={() => { setOpen(false); setConfirm(''); }} fullWidth variant="outlined"
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, borderColor: '#e5e7eb', color: '#6b7280', '&:hover': { borderColor: '#9ca3af' } }}>
            Cancel
          </Button>
          <Button variant="contained" disabled={confirm !== 'DELETE'} onClick={handleDelete} fullWidth
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700,
              background: confirm === 'DELETE' ? 'linear-gradient(135deg, #ef4444, #dc2626)' : '#e5e7eb',
              color: confirm === 'DELETE' ? '#fff' : '#9ca3af',
              boxShadow: confirm === 'DELETE' ? '0 4px 15px rgba(239,68,68,0.4)' : 'none',
              '&:hover': { background: 'linear-gradient(135deg, #dc2626, #b91c1c)' } }}>
            Delete Forever
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
